import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AsyncSearchInput } from "../AsyncSearchInput/AsyncSearchInput";
import { SelectOption } from "../AsyncSearchInput/types";
import { TAXONOMY_CLIENT } from "../../../utils/consts";
import {
  MENTION_NAME_QUERY,
  MENTION_ID_QUERY,
} from "../../../apis/taxonomy/queries";
import { HgMention } from "../../../types/taxonomy";
import { useLazyQuery } from "@apollo/client";

export type MentionOption = SelectOption;

interface MentionQueryResponse {
  mention: {
    data: HgMention[];
  };
}

export interface MentionInputProps {
  onMentionsSelect: (mentions: MentionOption[]) => void;
  selectedMentionIds?: number[];
}

function HgMentionToSelectOption(mention: HgMention): MentionOption {
  return { label: mention.name, value: mention.id.toString() };
}

function convertHgMentionsToSelectOptionsArray(
  data?: HgMention[]
): MentionOption[] {
  if (!data || data.length === 0) {
    return [];
  }
  return data.map(HgMentionToSelectOption);
}

const DEFAULT_PLACEHOLDER = "Search...";
const NO_MENTIONS_MESSAGE = "No mentions found";

export const MentionInput: React.FC<MentionInputProps> = ({
  onMentionsSelect,
  selectedMentionIds = [],
}) => {
  const [currentSelectedMentions, setCurrentSelectedMentions] = useState<
    MentionOption[]
  >([]);

  const [fetchMentionsByIds, { loading }] = useLazyQuery<MentionQueryResponse>(
    MENTION_ID_QUERY,
    {
      context: { clientName: TAXONOMY_CLIENT },
    }
  );

  const handleOptionsSelect = useCallback(
    (options: MentionOption[]) => {
      setCurrentSelectedMentions(options);
      onMentionsSelect(options);
    },
    [onMentionsSelect]
  );

  const needToFetchMentionIds = useMemo(() => {
    const knownIds = new Set(
      currentSelectedMentions.map((mention) => Number(mention.value))
    );
    return (
      selectedMentionIds.length > 0 &&
      !selectedMentionIds.every((id: number) => knownIds.has(id))
    );
  }, [selectedMentionIds, currentSelectedMentions]);

  useEffect(() => {
    if (selectedMentionIds.length === 0) {
      setCurrentSelectedMentions([]);
      return;
    }

    const fetchMentions = async () => {
      const { data } = await fetchMentionsByIds({
        variables: {
          ids: selectedMentionIds,
        },
      });
      if (data?.mention?.data) {
        const options = convertHgMentionsToSelectOptionsArray(
          data.mention.data
        );
        setCurrentSelectedMentions(options);
      }
    };

    if (needToFetchMentionIds) {
      fetchMentions();
    }
  }, [selectedMentionIds, needToFetchMentionIds, fetchMentionsByIds]);

  return (
    <AsyncSearchInput<MentionOption, HgMention[]>
      id="mentions"
      query={MENTION_NAME_QUERY}
      clientName={TAXONOMY_CLIENT}
      searchKey="name"
      selectedOptions={currentSelectedMentions}
      onOptionsSelect={handleOptionsSelect}
      convertResponseToOptions={convertHgMentionsToSelectOptionsArray}
      responseDataPath="mention.data"
      placeholderText={DEFAULT_PLACEHOLDER}
      noResultsMessage={NO_MENTIONS_MESSAGE}
      showGlobalLoadingIndicator={loading}
    />
  );
};
